import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clock,
  ExternalLink,
  FileCheck2,
  Loader2,
  RotateCw,
} from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * EmployeeAssignmentsDrawer – P1 (Juni 2026)
 *
 * Wird aus der Mitarbeiterliste über die klickbaren KPIs geöffnet. Trennt
 * AKTUELLEN Handlungsbedarf von GEPLANTEN Wiederholungen (rein abgeleitet,
 * `action_category` aus dem Backend – keine Statusänderung). Beim Klick auf
 * „Handlungsbedarf" werden BEIDE Bereiche gezeigt:
 *   1. „Jetzt zu erledigen"  (action_category === "handlungsbedarf")
 *   2. „Geplante Wiederholungen" (action_category === "geplant")
 *
 * Tenant-Sicherheit: alle API-Calls laufen über Owner-Auth; der Backend-Layer
 * filtert implizit über tenant_id.
 */

const CATEGORY_META = {
  handlungsbedarf: { title: "Handlungsbedarf", icon: Clock, iconColor: "text-amber-700" },
  bald_faellig: { title: "Bald fällig", icon: RotateCw, iconColor: "text-sky-700" },
  ueberfaellig: { title: "Überfällig", icon: AlertTriangle, iconColor: "text-rose-700" },
  geplant: { title: "Geplante Wiederholungen", icon: CalendarClock, iconColor: "text-slate-400" },
  erledigt: { title: "Erledigte Aufgaben", icon: CheckCircle2, iconColor: "text-emerald-700" },
};

function formatDate(iso) {
  if (!iso) return "–";
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function daysBetween(isoA, isoB = new Date().toISOString()) {
  if (!isoA) return null;
  const a = new Date(isoA);
  const b = new Date(isoB);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  return Math.floor((b - a) / (1000 * 60 * 60 * 24));
}

function daysUntil(iso) {
  if (!iso) return null;
  return Math.floor((new Date(iso) - new Date()) / (1000 * 60 * 60 * 24));
}

function rowBadge(item, kind) {
  if (item.status === "erledigt")
    return (
      <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-800" data-testid={`drawer-row-status-${item.id}`}>
        Erledigt
      </Badge>
    );
  if (item.status === "ueberfaellig")
    return (
      <Badge variant="outline" className="bg-rose-50 border-rose-200 text-rose-800" data-testid={`drawer-row-status-${item.id}`}>
        Überfällig
      </Badge>
    );
  if (kind === "geplant")
    return (
      <Badge variant="outline" className="bg-slate-100 border-slate-200 text-slate-500" data-testid={`drawer-row-status-${item.id}`}>
        Geplant
      </Badge>
    );
  return (
    <Badge variant="outline" className="bg-amber-50 border-amber-200 text-amber-800" data-testid={`drawer-row-status-${item.id}`}>
      Offen
    </Badge>
  );
}

export default function EmployeeAssignmentsDrawer({
  open,
  onOpenChange,
  employee,
  category = "handlungsbedarf", // handlungsbedarf | bald_faellig | ueberfaellig | geplant | erledigt
}) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null); // null = loading
  const meta = CATEGORY_META[category] || CATEGORY_META.handlungsbedarf;
  const Icon = meta.icon;

  useEffect(() => {
    if (!open || !employee?.id) return;
    let cancelled = false;
    (async () => {
      setItems(null);
      try {
        const data = await api.listAssignments({ employee_id: employee.id });
        if (cancelled) return;
        setItems(data || []);
      } catch (e) {
        if (cancelled) return;
        toast.error("Aufgaben konnten nicht geladen werden.");
        setItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, employee?.id]);

  const sortByDue = (arr) =>
    [...arr].sort((a, b) => {
      const dueCmp = (a.due_date || "").localeCompare(b.due_date || "");
      if (dueCmp !== 0) return dueCmp;
      return (a.assigned_at || "").localeCompare(b.assigned_at || "");
    });

  // Bereiche je nach Kategorie ableiten (rein lesend).
  const sections = useMemo(() => {
    const all = items || [];
    const handlungsbedarf = sortByDue(all.filter((a) => a.action_category === "handlungsbedarf"));
    const geplant = sortByDue(all.filter((a) => a.action_category === "geplant"));
    const ueberfaellig = sortByDue(all.filter((a) => a.status === "ueberfaellig"));
    const baldFaellig = sortByDue(
      all.filter((a) => {
        if (a.status !== "offen" || !a.due_date) return false;
        const d = daysUntil(a.due_date);
        return d !== null && d >= 0 && d <= 30;
      })
    );
    const erledigt = [...all]
      .filter((a) => a.status === "erledigt")
      .sort((a, b) => (b.completed_at || "").localeCompare(a.completed_at || ""));

    if (category === "handlungsbedarf") {
      return [
        {
          key: "jetzt",
          kind: "handlungsbedarf",
          title: "Jetzt zu erledigen",
          items: handlungsbedarf,
          emptyText: "Aktuell besteht kein Handlungsbedarf.",
        },
        {
          key: "geplant",
          kind: "geplant",
          title: "Geplante Wiederholungen",
          items: geplant,
          emptyText: "Keine geplanten Wiederholungen.",
          muted: true,
        },
      ];
    }
    if (category === "geplant")
      return [{ key: "geplant", kind: "geplant", title: "Geplante Wiederholungen", items: geplant, emptyText: "Keine geplanten Wiederholungen.", muted: true }];
    if (category === "ueberfaellig")
      return [{ key: "ueberfaellig", kind: "ueberfaellig", title: "Überfällige Aufgaben", items: ueberfaellig, emptyText: "Keine überfälligen Aufgaben. ✓" }];
    if (category === "bald_faellig")
      return [{ key: "bald", kind: "handlungsbedarf", title: "Bald fällig (≤ 30 Tage)", items: baldFaellig, emptyText: "Nichts wird in den nächsten 30 Tagen fällig. ✓" }];
    return [{ key: "erledigt", kind: "erledigt", title: "Erledigte Aufgaben", items: erledigt, emptyText: "Noch keine erledigten Aufgaben." }];
  }, [items, category]);

  const employeeName = employee
    ? `${employee.first_name || ""} ${employee.last_name || ""}`.trim()
    : "";

  const handleOpen = (item) => {
    if (item.status === "erledigt") {
      if (item.nachweis_id) {
        navigate(`/unternehmer/nachweis/${item.nachweis_id}`);
      } else {
        toast.message("Kein Nachweis zu dieser Aufgabe gefunden.");
        return;
      }
    } else if (item.course?.id) {
      navigate(`/unternehmer/welt/${item.course.id}`);
    } else {
      toast.message("Welt nicht gefunden.");
      return;
    }
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto"
        data-testid="employee-assignments-drawer"
      >
        <SheetHeader>
          <SheetTitle
            className="font-heading text-xl flex items-center gap-2"
            data-testid="drawer-title"
          >
            <Icon className={cn("w-5 h-5 shrink-0", meta.iconColor)} />
            <span>
              {meta.title} – {employeeName}
            </span>
          </SheetTitle>
          <SheetDescription className="sr-only">
            {meta.title} für {employeeName}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-6">
          {items === null ? (
            <div className="py-12 text-center text-slate-500">
              <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" />
              <p className="text-sm">Wird geladen…</p>
            </div>
          ) : (
            sections.map((section) => (
              <div key={section.key} data-testid={`drawer-section-${section.key}`}>
                <div
                  className={cn(
                    "text-xs uppercase tracking-widest font-medium mb-2",
                    section.muted ? "text-slate-400" : "text-slate-500"
                  )}
                >
                  {section.title}
                  <span className="ml-2 text-slate-400 normal-case tracking-normal">
                    ({section.items.length})
                  </span>
                </div>
                {section.items.length === 0 ? (
                  <div
                    className="py-6 text-center text-slate-500 border border-dashed border-slate-200 rounded-md"
                    data-testid={`drawer-empty-${section.key}`}
                  >
                    <FileCheck2 className="w-7 h-7 mx-auto text-slate-300 mb-1.5" />
                    <p className="text-sm">{section.emptyText}</p>
                  </div>
                ) : (
                  <ul
                    className={cn(
                      "divide-y divide-slate-100 border rounded-md overflow-hidden",
                      section.muted ? "border-slate-200 bg-slate-50/40" : "border-slate-200"
                    )}
                    data-testid={`drawer-list-${section.key}`}
                  >
                    {section.items.map((item) => {
                      const courseTitle = item.course?.title || "Welt entfernt";
                      const version = item.course?.version || item.course_version || "1.0";
                      const dueDate = item.due_date;
                      const doneDate = item.completed_at;
                      return (
                        <li
                          key={item.id}
                          className={cn(
                            "px-4 py-3 hover:bg-slate-50 transition-colors",
                            section.muted ? "bg-transparent" : "bg-white"
                          )}
                          data-testid={`drawer-row-${item.id}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-baseline gap-2 flex-wrap">
                                <div
                                  className="font-medium text-slate-900 truncate"
                                  data-testid={`drawer-row-title-${item.id}`}
                                >
                                  {courseTitle}
                                </div>
                                <span className="text-[11px] font-mono text-slate-500">
                                  v{version}
                                </span>
                                {rowBadge(item, section.kind)}
                              </div>
                              <div className="text-xs text-slate-500 mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5">
                                {item.status === "erledigt" ? (
                                  <span data-testid={`drawer-row-date-${item.id}`}>
                                    Erledigt am {formatDate(doneDate)}
                                  </span>
                                ) : (
                                  <span data-testid={`drawer-row-date-${item.id}`}>
                                    Fällig am {formatDate(dueDate)}
                                    {dueDate && (() => {
                                      const d = daysBetween(new Date().toISOString(), dueDate);
                                      if (d === null) return null;
                                      if (d > 0) return ` (in ${d} Tagen)`;
                                      if (d === 0) return " (heute)";
                                      return ` (${Math.abs(d)} Tage überfällig)`;
                                    })()}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpen(item)}
                              data-testid={`drawer-row-open-${item.id}`}
                              className="h-8 shrink-0"
                            >
                              <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                              {item.status === "erledigt" ? "Nachweis" : "Öffnen"}
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
